import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { buildDialogThemeOverrides } from '../shared/theme/dialogOverrides';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import HotelRoundedIcon from '@mui/icons-material/HotelRounded';
import MeetingRoomRoundedIcon from '@mui/icons-material/MeetingRoomRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import HowToRegRoundedIcon from '@mui/icons-material/HowToRegRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import moment from 'moment';
import {
  extractPublicWebCheckInIdDocument,
  getPublicWebCheckInInvite,
  startPublicWebCheckInPayment,
  submitPublicWebCheckIn,
  verifyPublicWebCheckInPayment,
} from '../services/api';
import countriesData from '../data/countries-states-cities-data';
import countryCodesData from '../data/country-codes.json';
import { cityFilterOptions, countryFilterOptions, stateFilterOptions } from '../utils/locationSearch';
import { buildRazorpayContactPrefill, loadRazorpayScript } from '../nexastay/nexastayPayment';
import { bumpDashboardDataRevision } from '../utils/dashboardRefreshCoordinator';
import {
  formatAadhaarDisplay,
  isAadhaarDocumentType,
  normalizeDocumentIdForSubmit,
  normalizeDocumentIdInput,
  validateAadhaarIdNumber,
  validateGuestDocumentImages,
} from '../utils/documentIdFormatters';
import {
  formatPhoneLengthLabel,
  getPhoneMaxLength,
  normalizePhoneInputForCountry,
  validatePhoneForCountry,
} from '../utils/phoneValidation';

const accent = '#4ce1b6';

/** Stack above SupportChatFab / default chrome; nested camera must sit above AI dialog. */
const AI_PROMPT_DIALOG_Z = 1400;
const NESTED_DIALOG_Z = 1500;

/** Guest check-in is always dark, regardless of staff theme preference in localStorage. */
const webCheckInDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: accent },
    background: {
      default: '#070b14',
      paper: '#0b1220',
    },
    text: {
      primary: '#f8fafc',
      secondary: '#cbd5e1',
    },
  },
  components: {
    ...buildDialogThemeOverrides({ accentColor: accent }),
  },
});

const purposeOptions = [
  { value: 'business', label: 'Business' },
  { value: 'leisure', label: 'Leisure' },
  { value: 'medical', label: 'Medical' },
  { value: 'family_visit', label: 'Family Visit' },
  { value: 'event', label: 'Event' },
  { value: 'transit', label: 'Transit' },
  { value: 'education', label: 'Education' },
  { value: 'government', label: 'Government' },
  { value: 'other', label: 'Other' },
];

const documentOptions = [
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'voter_id', label: 'Voter ID' },
  { value: 'driving_license', label: 'Driving Licence' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'government_id', label: 'Government ID' },
  { value: 'other', label: 'Other' },
];

const titleOptions = ['', 'Mr', 'Mrs', 'Ms', 'Dr', 'Prof'];

function isIndiaCountry(country) {
  const value = String(country || '').trim().toLowerCase();
  return value === 'india' || value === 'in' || value === 'ind';
}

function defaultDocumentTypeForCountry(country) {
  return isIndiaCountry(country) ? 'aadhaar' : 'passport';
}

const initialGuest = {
  title: '',
  fullName: '',
  email: '',
  phone: '',
  gender: '',
  dob: '',
  address: '',
  country: 'India',
  state: '',
  city: '',
  pincode: '',
  purpose_of_stay: '',
  purpose_of_stay_note: '',
  document_type: 'aadhaar',
  id_number: '',
  id_front: '',
  id_back: '',
};

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function dataUrlToFile(dataUrl, filename) {
  const [meta, content] = String(dataUrl || '').split(',');
  const mime = /data:(.*?);base64/.exec(meta || '')?.[1] || 'image/jpeg';
  const binary = atob(content || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}

function splitName(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return { first_name: '', last_name: '' };
  if (parts.length === 1) return { first_name: parts[0], last_name: '' };
  return { first_name: parts[0], last_name: parts.slice(1).join(' ') };
}

const SORTED_PHONE_DIAL_CODES = [...countryCodesData].sort(
  (a, b) => b.dialCode.length - a.dialCode.length
);

/** Split a stored phone into dial code + national digits (defaults to +91). */
function parsePhoneWithDialCode(rawPhone, fallbackDialCode = '+91') {
  const phoneValue = String(rawPhone || '').trim();
  const fallback = String(fallbackDialCode || '+91').trim() || '+91';
  if (!phoneValue) {
    return { dialCode: fallback, nationalNumber: '' };
  }

  // Only strip an embedded dial code when the value is explicitly international.
  // Bare national numbers like "82874402" must not be guessed as "+82…".
  const compactWithPlus = phoneValue.startsWith('+')
    ? `+${phoneValue.slice(1).replace(/\D/g, '')}`
    : phoneValue.startsWith('00')
      ? `+${phoneValue.slice(2).replace(/\D/g, '')}`
      : '';
  if (compactWithPlus) {
    const matched = SORTED_PHONE_DIAL_CODES.find((country) =>
      compactWithPlus.startsWith(country.dialCode)
    );
    if (matched) {
      return {
        dialCode: matched.dialCode,
        nationalNumber: normalizePhoneInputForCountry(
          compactWithPlus.slice(matched.dialCode.length),
          matched.dialCode
        ),
      };
    }
  }

  const digits = phoneValue.replace(/\D/g, '');
  return {
    dialCode: fallback,
    nationalNumber: normalizePhoneInputForCountry(digits, fallback),
  };
}

function normalizeInviteDialCode(rawDialCode, fallbackDialCode = '+91') {
  const fallback = String(fallbackDialCode || '+91').trim() || '+91';
  const digits = String(rawDialCode || '').replace(/\D/g, '');
  if (!digits) return fallback;
  const withPlus = `+${digits}`;
  const matched = countryCodesData.find((country) => country.dialCode === withPlus);
  return matched?.dialCode || withPlus;
}

function formatMoney(amount, currency = 'INR') {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) return '';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: String(currency || 'INR').toUpperCase(),
      maximumFractionDigits: 2,
    }).format(numeric);
  } catch (error) {
    return `${currency || 'INR'} ${numeric.toFixed(2)}`;
  }
}

function formatStayDate(value, { short = false } = {}) {
  const m = moment(value);
  if (!m.isValid()) return '—';
  return short ? m.format('DD MMM') : m.format('DD MMM YYYY');
}

function formatLinkExpiryPreset(minutes) {
  const value = Number(minutes);
  if (!Number.isFinite(value) || value <= 0) return '';
  if (value < 60) return `${value} min`;
  if (value % 1440 === 0) {
    const days = value / 1440;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (value % 60 === 0) {
    const hours = value / 60;
    return `${hours} hr`;
  }
  return `${value} min`;
}

function paymentHeadline(invite) {
  const req = invite?.payment_requirement;
  if (req === 'full') return 'Full payment online';
  if (req === 'partial') return 'Partial payment online';
  if (req === 'pay_at_hotel') return 'Pay at hotel';
  return 'No payment required';
}

function formatExpiryCountdown(expiresAt, nowMs = Date.now()) {
  const end = moment(expiresAt);
  if (!end.isValid()) return { label: '—', expired: false };
  const remainingMs = end.valueOf() - nowMs;
  if (remainingMs <= 0) return { label: 'Expired', expired: true };
  const totalSec = Math.floor(remainingMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  if (days > 0) return { label: `${days}d ${hours}h left`, expired: false };
  if (hours > 0) return { label: `${hours}h ${String(mins).padStart(2, '0')}m left`, expired: false };
  return {
    label: `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')} left`,
    expired: false,
  };
}

function CheckInCompletedPanel({ invite, variant = 'completed', paid = false }) {
  const hotelName = invite?.hotel?.name || 'the hotel';
  const hotelPhone = (invite?.hotel?.phone || '').trim();
  const hotelEmail = (invite?.hotel?.email || '').trim();
  const isFresh = variant === 'success';

  return (
    <Box
      sx={{
        width: '100%',
        py: { xs: 2, md: 3.5 },
        px: { xs: 0.5, md: 1 },
        boxSizing: 'border-box',
      }}
    >
      <Stack spacing={2.5} alignItems="center" sx={{ maxWidth: 520, mx: 'auto', textAlign: 'center' }}>
        <Box
          sx={{
            width: 88,
            height: 88,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            bgcolor: alpha(accent, 0.14),
            border: `1px solid ${alpha(accent, 0.42)}`,
            boxShadow: `0 16px 40px ${alpha(accent, 0.22)}`,
          }}
        >
          <CheckCircleIcon sx={{ fontSize: 52, color: accent }} />
        </Box>

        <Box>
          <Typography
            sx={{
              fontWeight: 900,
              fontSize: { xs: '1.35rem', md: '1.6rem' },
              letterSpacing: -0.4,
              lineHeight: 1.2,
              color: '#f8fafc',
            }}
          >
            {isFresh
              ? (paid ? 'Payment & check-in complete' : 'Web check-in complete')
              : 'Web check-in already completed'}
          </Typography>
          <Typography
            sx={{
              mt: 1.1,
              color: '#cbd5e1',
              fontSize: { xs: '0.95rem', md: '1.02rem' },
              fontWeight: 600,
              lineHeight: 1.55,
            }}
          >
            {isFresh
              ? (paid
                ? `Your payment was received and your details are with ${hotelName}. You’re all set for arrival.`
                : `Your details have been securely submitted to ${hotelName}. You’re all set for arrival.`)
              : `This web check-in was completed successfully. Your reservation details are already with ${hotelName}.`}
          </Typography>
          <Typography
            sx={{
              mt: 1,
              color: '#94a3b8',
              fontSize: '0.88rem',
              fontWeight: 500,
              lineHeight: 1.55,
            }}
          >
            Please arrive with a valid government ID. For changes or more information, contact the hotel directly.
          </Typography>
        </Box>

        {(invite?.room?.number || invite?.check_in_date || hotelName) ? (
          <Box
            sx={{
              width: '100%',
              px: 1.75,
              py: 1.5,
              borderRadius: 2.5,
              bgcolor: alpha('#ffffff', 0.04),
              border: `1px solid ${alpha('#ffffff', 0.1)}`,
            }}
          >
            <Stack spacing={0.65}>
              <Typography sx={{ color: '#e2e8f0', fontWeight: 800, fontSize: '0.95rem' }}>
                {hotelName}
              </Typography>
              <Typography sx={{ color: accent, fontWeight: 900, fontSize: '0.9rem' }}>
                {[
                  invite?.room?.number ? `Room ${invite.room.number}` : null,
                  invite?.room?.room_type_name || null,
                ].filter(Boolean).join(' · ') || 'Your stay'}
              </Typography>
              {invite?.check_in_date ? (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.8rem', fontWeight: 600 }}>
                  Check-in {formatStayDate(invite.check_in_date, { short: true })}
                  {invite?.check_out_date
                    ? ` → ${formatStayDate(invite.check_out_date, { short: true })}`
                    : ''}
                </Typography>
              ) : null}
            </Stack>
          </Box>
        ) : null}

        <Box
          sx={{
            width: '100%',
            px: 1.75,
            py: 1.5,
            borderRadius: 2.5,
            bgcolor: alpha(accent, 0.08),
            border: `1px solid ${alpha(accent, 0.22)}`,
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="flex-start">
            <SupportAgentRoundedIcon sx={{ color: accent, mt: 0.15, flexShrink: 0 }} />
            <Box sx={{ textAlign: 'left', minWidth: 0 }}>
              <Typography sx={{ color: '#f8fafc', fontWeight: 800, fontSize: '0.9rem' }}>
                Need help?
              </Typography>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem', mt: 0.35, lineHeight: 1.5 }}>
                Contact {hotelName} for stay updates, special requests, or arrival assistance.
              </Typography>
              {(hotelPhone || hotelEmail) ? (
                <Stack spacing={0.65} sx={{ mt: 1.15 }}>
                  {hotelPhone ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <PhoneRoundedIcon sx={{ fontSize: 16, color: accent }} />
                      <Typography
                        component="a"
                        href={`tel:${hotelPhone.replace(/\s+/g, '')}`}
                        sx={{ color: accent, fontWeight: 800, fontSize: '0.86rem', textDecoration: 'none' }}
                      >
                        {hotelPhone}
                      </Typography>
                    </Stack>
                  ) : null}
                  {hotelEmail ? (
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <EmailRoundedIcon sx={{ fontSize: 16, color: accent }} />
                      <Typography
                        component="a"
                        href={`mailto:${hotelEmail}`}
                        sx={{ color: accent, fontWeight: 800, fontSize: '0.86rem', textDecoration: 'none', wordBreak: 'break-all' }}
                      >
                        {hotelEmail}
                      </Typography>
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Box>
  );
}

function StaySummaryCard({ invite }) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!invite?.expires_at) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [invite?.expires_at]);

  if (!invite) return null;

  const roomImage = invite?.room?.image_url || '';
  const roomNumber = invite?.room?.number || '—';
  const roomType = invite?.room?.room_type_name || 'Room';
  const nights = Number(invite?.nights);
  const nightsLabel = Number.isFinite(nights) && nights > 0
    ? `${nights} night${nights === 1 ? '' : 's'}`
    : 'Stay';
  const totalLabel = formatMoney(invite.booking_total_amount, invite.payment_currency);
  const payNowLabel = formatMoney(invite.payment_amount, invite.payment_currency);
  const showPayNow = ['full', 'partial'].includes(invite.payment_requirement) && Boolean(payNowLabel);
  const amountPrimary = showPayNow
    ? payNowLabel
    : (totalLabel || (invite.payment_requirement === 'none' ? 'Free' : '—'));
  const amountSecondary = showPayNow && totalLabel && invite.payment_requirement === 'partial'
    ? `Total ${totalLabel}`
    : invite.payment_requirement === 'pay_at_hotel' && totalLabel
      ? 'Due at hotel'
      : invite.payment_requirement === 'full' && totalLabel
        ? 'Pay online'
        : invite.payment_requirement === 'none'
          ? 'No payment due'
          : '';
  const expiryWindow = formatLinkExpiryPreset(invite.link_expiry_minutes);
  const expiry = formatExpiryCountdown(invite.expires_at, nowMs);
  const expiryTone = expiry.expired ? '#fb7185' : accent;
  const paymentChipLabel = invite.payment_requirement_display || paymentHeadline(invite);

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        borderRadius: { xs: 2.5, sm: 3 },
        border: `1px solid ${alpha(accent, 0.28)}`,
        background: `
          radial-gradient(ellipse 70% 90% at 0% 0%, ${alpha(accent, 0.18)} 0%, transparent 55%),
          linear-gradient(135deg, ${alpha('#0f172a', 0.98)} 0%, ${alpha('#07111f', 1)} 100%)
        `,
        boxShadow: `0 14px 36px ${alpha('#000', 0.32)}`,
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Stack direction="row" alignItems="stretch" sx={{ width: '100%', minWidth: 0 }}>
        <Box
          sx={{
            position: 'relative',
            width: { xs: 112, sm: 152 },
            flexShrink: 0,
            alignSelf: 'stretch',
            overflow: 'hidden',
          }}
        >
          {roomImage ? (
            <Box
              component="img"
              src={roomImage}
              alt={`${roomType} room ${roomNumber}`}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: { xs: 148, sm: 168 },
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <Box
              sx={{
                height: '100%',
                minHeight: { xs: 148, sm: 168 },
                display: 'grid',
                placeItems: 'center',
                background: `linear-gradient(165deg, ${alpha(accent, 0.28)} 0%, ${alpha('#0f172a', 0.96)} 100%)`,
              }}
            >
              <HotelRoundedIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: alpha(accent, 0.85) }} />
            </Box>
          )}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(180deg, ${alpha('#020617', 0.08)} 0%, ${alpha('#020617', 0.78)} 100%)`,
            }}
          />
          <Chip
            size="small"
            icon={<MeetingRoomRoundedIcon sx={{ fontSize: '12px !important' }} />}
            label={roomNumber}
            sx={{
              position: 'absolute',
              left: 6,
              bottom: 6,
              height: 22,
              maxWidth: 'calc(100% - 12px)',
              fontWeight: 900,
              fontSize: '0.68rem',
              bgcolor: accent,
              color: '#031512',
              boxShadow: `0 6px 14px ${alpha('#000', 0.35)}`,
              '& .MuiChip-icon': { color: '#031512', ml: 0.45 },
              '& .MuiChip-label': { px: 0.55 },
            }}
          />
        </Box>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            p: { xs: 1.15, sm: 1.35 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '0.95rem', sm: '1.05rem' },
                  letterSpacing: -0.25,
                  lineHeight: 1.15,
                  color: '#f8fafc',
                }}
                noWrap
              >
                {roomType}
              </Typography>
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: '0.72rem',
                  mt: 0.25,
                  fontWeight: 600,
                  lineHeight: 1.25,
                }}
                noWrap
              >
                {invite.hotel?.name || 'Hotel'}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={paymentChipLabel}
              sx={{
                height: 24,
                flexShrink: 0,
                fontWeight: 800,
                fontSize: '0.66rem',
                bgcolor: alpha(accent, 0.14),
                color: accent,
                border: `1px solid ${alpha(accent, 0.32)}`,
                '& .MuiChip-label': { px: 0.9 },
              }}
            />
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr',
              alignItems: 'center',
              gap: { xs: 0.55, sm: 0.75 },
              px: { xs: 0.85, sm: 1 },
              py: { xs: 0.75, sm: 0.85 },
              borderRadius: 2,
              bgcolor: alpha('#ffffff', 0.035),
              border: `1px solid ${alpha('#ffffff', 0.08)}`,
            }}
          >
            <Box sx={{ minWidth: 0, textAlign: 'left' }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.4, color: '#94a3b8', textTransform: 'uppercase' }}>
                Check-in
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: '#f8fafc', lineHeight: 1.2 }} noWrap>
                {formatStayDate(invite.check_in_date, { short: true })}
              </Typography>
            </Box>

            <Stack alignItems="center" spacing={0.15} sx={{ px: 0.25 }}>
              <Box
                sx={{
                  px: 0.85,
                  py: 0.2,
                  borderRadius: 999,
                  bgcolor: alpha(accent, 0.16),
                  border: `1px solid ${alpha(accent, 0.3)}`,
                  color: accent,
                  fontSize: '0.64rem',
                  fontWeight: 900,
                  lineHeight: 1.4,
                  whiteSpace: 'nowrap',
                }}
              >
                {nightsLabel}
              </Box>
              <Box
                sx={{
                  width: { xs: 28, sm: 40 },
                  height: 2,
                  borderRadius: 1,
                  background: `linear-gradient(90deg, transparent, ${alpha(accent, 0.75)}, transparent)`,
                }}
              />
            </Stack>

            <Box sx={{ minWidth: 0, textAlign: 'right' }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.4, color: '#94a3b8', textTransform: 'uppercase' }}>
                Check-out
              </Typography>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: '#f8fafc', lineHeight: 1.2 }} noWrap>
                {formatStayDate(invite.check_out_date, { short: true })}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                px: 0.9,
                py: 0.7,
                borderRadius: 1.75,
                bgcolor: alpha(expiryTone, expiry.expired ? 0.12 : 0.08),
                border: `1px solid ${alpha(expiryTone, expiry.expired ? 0.38 : 0.24)}`,
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={0.45} alignItems="center" sx={{ color: expiryTone, mb: 0.15 }}>
                <ScheduleRoundedIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.35, textTransform: 'uppercase', color: alpha(expiryTone, 0.9) }}>
                  Link
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.8rem', sm: '0.86rem' }, color: expiryTone, lineHeight: 1.2 }} noWrap>
                {expiry.label}
              </Typography>
              {expiryWindow ? (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.62rem', mt: 0.1, lineHeight: 1.2 }} noWrap>
                  {expiryWindow} window
                </Typography>
              ) : null}
            </Box>

            <Box
              sx={{
                px: 0.9,
                py: 0.7,
                borderRadius: 1.75,
                bgcolor: alpha(accent, 0.08),
                border: `1px solid ${alpha(accent, 0.24)}`,
                minWidth: 0,
              }}
            >
              <Stack direction="row" spacing={0.45} alignItems="center" sx={{ color: accent, mb: 0.15 }}>
                <PaymentsRoundedIcon sx={{ fontSize: 14 }} />
                <Typography sx={{ fontSize: '0.6rem', fontWeight: 800, letterSpacing: 0.35, textTransform: 'uppercase', color: alpha(accent, 0.9) }}>
                  {showPayNow ? 'Pay now' : 'Amount'}
                </Typography>
              </Stack>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '0.8rem', sm: '0.86rem' }, color: '#f8fafc', lineHeight: 1.2 }} noWrap>
                {amountPrimary}
              </Typography>
              {amountSecondary ? (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.62rem', mt: 0.1, lineHeight: 1.2 }} noWrap>
                  {amountSecondary}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Stack>
    </Box>
  );
}

const FIELD_ORDER = [
  'phone',
  'fullName',
  'email',
  'gender',
  'dob',
  'country',
  'state',
  'city',
  'pincode',
  'purpose_of_stay',
  'purpose_of_stay_note',
  'document_type',
  'id_number',
  'id_front',
];

function firstErrorField(errors) {
  return FIELD_ORDER.find((key) => Boolean(errors?.[key])) || Object.keys(errors || {})[0] || '';
}

function validateDateOfBirth(dobValue) {
  if (!dobValue) return 'Date of birth is required.';
  const dob = moment(dobValue, 'YYYY-MM-DD', true);
  if (!dob.isValid()) return 'Enter a valid date of birth.';
  if (dob.isAfter(moment(), 'day')) return 'Date of birth cannot be in the future.';
  const age = moment().diff(dob, 'years');
  if (age < 18) return 'Guest must be at least 18 years old.';
  return '';
}

function validateDocumentIdNumber(documentType, idNumber) {
  const docType = String(documentType || '').trim();
  const raw = String(idNumber || '').trim();
  if (!docType || docType === 'no_document') return 'Document type is required.';
  if (!raw) return 'ID number is required.';

  const normalized = raw.replace(/\s+/g, '').toUpperCase();
  if (docType === 'aadhaar') {
    return validateAadhaarIdNumber(normalized);
  }
  if (docType === 'passport') {
    return /^[A-Z][0-9]{7}$/.test(normalized)
      ? ''
      : 'Passport number must be 1 letter followed by 7 digits (e.g. A1234567).';
  }
  if (docType === 'voter_id') {
    return /^[A-Z]{3}[0-9]{7}$/.test(normalized)
      ? ''
      : 'Voter ID must be 3 letters followed by 7 digits (e.g. ABC1234567).';
  }
  if (docType === 'pan') {
    return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalized)
      ? ''
      : 'PAN must be 5 letters, 4 digits, and 1 letter (e.g. ABCDE1234F).';
  }
  if (docType === 'driving_license') {
    return /^[A-Z0-9-]{10,20}$/.test(normalized)
      ? ''
      : 'Driving licence number must be 10-20 characters.';
  }
  return /^[A-Z0-9][A-Z0-9\-/]{3,29}$/.test(normalized)
    ? ''
    : 'ID number must be 4-30 characters (letters, numbers, -, /).';
}

export default function PublicWebCheckInPage() {
  const { token = '' } = useParams();
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const phoneCountryCodeAnchorRef = useRef(null);

  const [invite, setInvite] = useState(null);
  const [guest, setGuest] = useState(initialGuest);
  const [phoneCountryCode, setPhoneCountryCode] = useState('+91');
  const [phoneError, setPhoneError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [cameraUpload, setCameraUpload] = useState(false);
  const [cameraTarget, setCameraTarget] = useState('');
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [paymentFailure, setPaymentFailure] = useState('');
  const [completionVariant, setCompletionVariant] = useState(''); // 'success' | 'completed' | ''
  const [aiPromptOpen, setAiPromptOpen] = useState(false);

  const checkOutDate = useMemo(() => (
    invite?.check_out_date
    || moment(invite?.check_in_date || undefined).add(1, 'day').format('YYYY-MM-DD')
    || moment().add(1, 'day').format('YYYY-MM-DD')
  ), [invite?.check_in_date, invite?.check_out_date]);
  const paymentRequired = Boolean(
    invite?.payment_requirement
      && ['full', 'partial'].includes(invite.payment_requirement)
      && Number(invite?.payment_amount || 0) > 0
  );
  const paymentAmountLabel = formatMoney(invite?.payment_amount, invite?.payment_currency);
  const bookingTotalLabel = formatMoney(invite?.booking_total_amount, invite?.payment_currency);
  const canUseAiWebCheckIn = invite?.ai_checkin_enabled !== false;

  useEffect(() => {
    let cancelled = false;
    async function loadInvite() {
      setLoading(true);
      setError('');
      try {
        const res = await getPublicWebCheckInInvite(token);
        if (cancelled) return;
        const nextInvite = res.invite;
        setInvite(nextInvite);
        if (nextInvite?.status === 'submitted') {
          setCompletionVariant('completed');
          setAiPromptOpen(false);
        } else {
          setCompletionVariant('');
          setAiPromptOpen(Boolean(nextInvite?.ai_checkin_enabled !== false));
        }
        const inviteDialCode = normalizeInviteDialCode(
          nextInvite?.guest_country_code,
          '+91'
        );
        const parsed = parsePhoneWithDialCode(
          nextInvite?.guest_phone || '',
          inviteDialCode
        );
        setPhoneCountryCode(parsed.dialCode);
        setPhoneError('');
        setGuest((prev) => ({
          ...prev,
          fullName: nextInvite?.guest_name || '',
          email: nextInvite?.guest_email || '',
          phone: parsed.nationalNumber,
        }));
      } catch (err) {
        if (!cancelled) setError(err?.userMessage || 'Unable to load this web check-in link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadInvite();
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [token]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search || '');
    const result = params.get('payment_result');
    if (!result) return;
    const message = params.get('message') || '';
    if (result === 'success') {
      bumpDashboardDataRevision('web_checkin_payment_success');
      setInvite((prev) => (prev ? { ...prev, status: 'submitted', payment_status: 'paid' } : prev));
      setCompletionVariant('success');
      setAiPromptOpen(false);
    } else {
      setPaymentFailure(message || 'Payment could not be completed. Please try again.');
      setError(message || 'Payment could not be completed. Please try again.');
    }
    params.delete('payment_result');
    params.delete('message');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, []);

  const isLinkInactive = invite && invite.status !== 'pending';
  const isSubmittedInvite = Boolean(
    completionVariant
    || invite?.status === 'submitted'
  );
  const isPaidCompletedInvite =
    invite?.status === 'submitted' &&
    invite?.payment_requirement &&
    invite.payment_requirement !== 'none' &&
    invite?.payment_status === 'paid';
  const showCompletedPanel = Boolean(isSubmittedInvite && !loading);
  const showInactiveOnly = Boolean(isLinkInactive && !isSubmittedInvite && !loading);
  const phoneLocked = Boolean(invite?.guest_phone);
  const selectedPhoneCountryCode = useMemo(
    () => countryCodesData.find((cc) => cc.dialCode === phoneCountryCode)
      || countryCodesData.find((cc) => cc.code === 'IN'),
    [phoneCountryCode]
  );
  const filteredPhoneCountryCodes = useMemo(() => {
    const search = phoneCountryCodeSearch.trim().toLowerCase();
    if (!search) return countryCodesData;
    return countryCodesData.filter((country) => (
      country.name.toLowerCase().includes(search)
      || country.dialCode.includes(search)
      || country.code.toLowerCase().includes(search)
    ));
  }, [phoneCountryCodeSearch]);

  const countries = useMemo(() => (
    countriesData.countries.map((country) => ({
      code: country.code,
      name: country.name,
    }))
  ), []);
  const selectedCountryObj = useMemo(() => {
    if (!guest.country) return null;
    return countriesData.countries.find((country) => (
      country.name === guest.country || country.code === guest.country
    )) || null;
  }, [guest.country]);
  const availableStates = selectedCountryObj?.states || [];
  const selectedStateObj = useMemo(() => {
    if (!guest.state) return null;
    return availableStates.find((state) => (
      state.name === guest.state || state.code === guest.state
    )) || null;
  }, [availableStates, guest.state]);
  const cityOptions = useMemo(() => {
    const base = selectedStateObj?.cities || [];
    const currentCity = String(guest.city || '').trim();
    if (currentCity && !base.some((city) => String(city).trim() === currentCity)) {
      return [currentCity, ...base];
    }
    return base;
  }, [guest.city, selectedStateObj]);

  const clearFieldError = (field) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    if (field === 'phone') setPhoneError('');
  };

  const clearFieldErrors = (fields = []) => {
    if (!fields.length) {
      setFieldErrors({});
      setPhoneError('');
      return;
    }
    setFieldErrors((prev) => {
      const next = { ...prev };
      fields.forEach((field) => {
        delete next[field];
      });
      return next;
    });
    if (fields.includes('phone')) setPhoneError('');
  };

  const updateGuest = (field) => (event) => {
    clearFieldError(field);
    setGuest((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const updatePhoneNumber = (event) => {
    if (phoneLocked) return;
    const nationalOnly = normalizePhoneInputForCountry(event.target.value, phoneCountryCode);
    setGuest((prev) => ({ ...prev, phone: nationalOnly }));
    const nextError = validatePhoneForCountry(nationalOnly, phoneCountryCode);
    setPhoneError(nextError);
    setFieldErrors((prev) => {
      if (!nextError && !prev.phone) return prev;
      if (!nextError) {
        const next = { ...prev };
        delete next.phone;
        return next;
      }
      return { ...prev, phone: nextError };
    });
  };

  const selectPhoneCountryCode = (country) => {
    if (phoneLocked) return;
    const normalizedPhone = normalizePhoneInputForCountry(guest.phone, country.dialCode);
    setPhoneCountryCode(country.dialCode);
    setGuest((prev) => ({ ...prev, phone: normalizedPhone }));
    const nextError = validatePhoneForCountry(normalizedPhone, country.dialCode);
    setPhoneError(nextError);
    setFieldErrors((prev) => {
      if (!nextError && !prev.phone) return prev;
      if (!nextError) {
        const next = { ...prev };
        delete next.phone;
        return next;
      }
      return { ...prev, phone: nextError };
    });
    setPhoneCountryCodeMenuOpen(false);
    setPhoneCountryCodeSearch('');
  };

  const updateDocumentType = (event) => {
    const value = event.target.value;
    clearFieldErrors(['document_type', 'id_number']);
    setGuest((prev) => ({
      ...prev,
      document_type: value,
      id_number: normalizeDocumentIdInput(value, prev.id_number),
    }));
  };

  const applyCountryChange = (countryName) => {
    const nextCountry = countryName || '';
    const nextDocumentType = defaultDocumentTypeForCountry(nextCountry);
    const documentChanged = guest.document_type !== nextDocumentType;
    if (documentChanged) {
      setFrontFile(null);
      setBackFile(null);
    }
    clearFieldErrors(['country', 'state', 'city', ...(documentChanged ? ['document_type', 'id_number', 'id_front', 'id_back'] : [])]);
    setGuest((prev) => ({
      ...prev,
      country: nextCountry,
      state: '',
      city: '',
      document_type: nextDocumentType,
      ...(documentChanged
        ? {
          id_number: '',
          id_front: '',
          id_back: '',
        }
        : {}),
    }));
  };

  const applyExtractedGuest = (extracted) => {
    if (!extracted) return;
    const name = [
      extracted.guest_first_name,
      extracted.guest_middle_name,
      extracted.guest_last_name,
    ].filter(Boolean).join(' ');
    setGuest((prev) => {
      const nextCountry = extracted.guest_country || prev.country;
      const extractedDocType = String(extracted.upload_document_type || '').trim();
      const nextDocumentType = (
        extractedDocType && extractedDocType !== 'no_document'
          ? extractedDocType
          : defaultDocumentTypeForCountry(nextCountry)
      );
      return {
        ...prev,
        title: extracted.guest_title || prev.title,
        fullName: name || prev.fullName,
        email: extracted.guest_email || prev.email,
        phone: prev.phone,
        gender: extracted.guest_gender || prev.gender,
        dob: extracted.guest_dob || prev.dob,
        address: extracted.guest_address || prev.address,
        country: nextCountry,
        state: extracted.guest_state || prev.state,
        city: extracted.guest_city || prev.city,
        pincode: extracted.guest_pincode || prev.pincode,
        document_type: nextDocumentType,
        id_number: extracted.document_id_number
          ? normalizeDocumentIdInput(nextDocumentType, extracted.document_id_number)
          : prev.id_number,
      };
    });
  };

  const updateDocumentIdNumber = (event) => {
    const value = event.target.value;
    clearFieldError('id_number');
    setGuest((prev) => ({
      ...prev,
      id_number: normalizeDocumentIdInput(prev.document_type, value),
    }));
  };

  const handleFileSelect = (side) => async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const dataUrl = await fileToDataUrl(file);
    if (side === 'front') {
      clearFieldError('id_front');
      setFrontFile(file);
      setGuest((prev) => ({ ...prev, id_front: dataUrl }));
    } else {
      clearFieldError('id_back');
      setBackFile(file);
      setGuest((prev) => ({ ...prev, id_back: dataUrl }));
    }
  };

  const handleUploadClick = (side) => {
    if (cameraUpload) {
      startCamera(side);
      return;
    }
    if (side === 'front') frontInputRef.current?.click();
    if (side === 'back') backInputRef.current?.click();
  };

  const startCamera = async (side) => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera is not available. Turn off Camera upload and choose a file.');
      return;
    }
    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      streamRef.current = stream;
      setCameraTarget(side);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 50);
    } catch {
      setError('Camera permission was denied or unavailable. Upload a file instead.');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraTarget('');
  };

  const captureCamera = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.86);
    const file = dataUrlToFile(dataUrl, `${cameraTarget || 'id'}-document.jpg`);
    if (cameraTarget === 'front') {
      clearFieldError('id_front');
      setFrontFile(file);
      setGuest((prev) => ({ ...prev, id_front: dataUrl }));
    } else {
      clearFieldError('id_back');
      setBackFile(file);
      setGuest((prev) => ({ ...prev, id_back: dataUrl }));
    }
    stopCamera();
  };

  const clearDocumentSide = (side) => {
    if (side === 'front') {
      clearFieldError('id_front');
      setFrontFile(null);
      setGuest((prev) => ({ ...prev, id_front: '' }));
      return;
    }
    clearFieldError('id_back');
    setBackFile(null);
    setGuest((prev) => ({ ...prev, id_back: '' }));
  };

  const handleAiAutofill = async () => {
    setError('');
    if (!canUseAiWebCheckIn) {
      setError('AI web check-in is not enabled for this hotel package.');
      return;
    }
    if (!frontFile) {
      setError('Upload or capture document front first.');
      return;
    }
    setAiLoading(true);
    try {
      const res = await extractPublicWebCheckInIdDocument(token, {
        front: frontFile,
        back: backFile,
      });
      applyExtractedGuest(res.guest);
      setAiPromptOpen(false);
    } catch (err) {
      setError(err?.userMessage || 'AI auto fill could not read this document.');
    } finally {
      setAiLoading(false);
    }
  };

  const validateFormFields = () => {
    const errors = {};
    if (!guest.fullName.trim()) errors.fullName = 'Name is required.';
    const nextPhoneError = validatePhoneForCountry(guest.phone, phoneCountryCode);
    if (nextPhoneError) {
      errors.phone = nextPhoneError.endsWith('.') ? nextPhoneError : `${nextPhoneError}.`;
    }
    if (!guest.email.trim()) {
      errors.email = 'Email address is required.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
    if (!guest.gender) errors.gender = 'Gender is required.';
    const dobError = validateDateOfBirth(guest.dob);
    if (dobError) errors.dob = dobError;
    if (!guest.country.trim()) errors.country = 'Country is required.';
    if (!guest.state.trim()) errors.state = 'State is required.';
    if (!guest.city.trim()) errors.city = 'City is required.';
    if (!guest.pincode.trim()) errors.pincode = 'Pincode is required.';
    if (!guest.purpose_of_stay) {
      errors.purpose_of_stay = 'Purpose of stay is required.';
    } else if (guest.purpose_of_stay === 'other' && !guest.purpose_of_stay_note.trim()) {
      errors.purpose_of_stay_note = 'Please describe the purpose of stay.';
    }
    if (!guest.document_type || guest.document_type === 'no_document') {
      errors.document_type = 'Document type is required.';
    } else {
      const documentError = validateDocumentIdNumber(guest.document_type, guest.id_number);
      if (documentError) errors.id_number = documentError;
      const imageErrors = validateGuestDocumentImages(
        guest.document_type,
        guest.id_front,
        guest.id_back,
      );
      if (imageErrors.id_front) errors.id_front = imageErrors.id_front;
      if (imageErrors.id_back) errors.id_back = imageErrors.id_back;
    }
    return errors;
  };

  const scrollToField = (field) => {
    if (!field || typeof document === 'undefined') return;
    const el = document.querySelector(`[data-field="${field}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.querySelector('input, textarea, [role="button"], .MuiSelect-select');
    if (focusable && typeof focusable.focus === 'function') {
      try {
        focusable.focus({ preventScroll: true });
      } catch (err) {
        focusable.focus();
      }
    }
  };

  const buildSubmitPayload = () => {
    const nameParts = splitName(guest.fullName);
    const normalizedDocumentType = guest.document_type === 'no_document' ? '' : guest.document_type;
    return {
      checkOutDate,
      guest: {
        title: guest.title,
        ...nameParts,
        email: guest.email.trim(),
        phone: guest.phone.trim().replace(/\D/g, ''),
        country_code: phoneCountryCode || '+91',
        gender: guest.gender,
        dob: guest.dob || null,
        address_line1: guest.address.trim(),
        country: guest.country.trim(),
        state: guest.state.trim(),
        city: guest.city.trim(),
        pincode: guest.pincode.trim(),
        purpose_of_stay: guest.purpose_of_stay,
        purpose_of_stay_note:
          guest.purpose_of_stay === 'other' ? guest.purpose_of_stay_note.trim() : '',
        document_type: normalizedDocumentType,
        id_number: normalizedDocumentType
          ? normalizeDocumentIdForSubmit(normalizedDocumentType, guest.id_number)
          : '',
        id_front: normalizedDocumentType ? guest.id_front : '',
        id_back: normalizedDocumentType ? guest.id_back : '',
      },
    };
  };

  const openRazorpayCheckout = (paymentOrder) => (
    new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        reject(new Error('Razorpay checkout is unavailable. Please try again.'));
        return;
      }
      let settled = false;
      const checkout = new window.Razorpay({
        key: paymentOrder.key_id,
        amount: paymentOrder.amount,
        currency: paymentOrder.currency || 'INR',
        name: paymentOrder.hotel_name || invite?.hotel?.name || 'RoomNexa',
        description: 'Web Check-in Payment',
        order_id: paymentOrder.order_id,
        retry: {
          enabled: false,
        },
        callback_url: paymentOrder.callback_url,
        redirect: Boolean(paymentOrder.callback_url),
        prefill: {
          name: guest.fullName,
          email: guest.email,
          contact: buildRazorpayContactPrefill(guest.phone, phoneCountryCode || '+91'),
        },
        theme: { color: accent },
        handler: (response) => {
          if (settled) return;
          settled = true;
          resolve({
            razorpay_order_id: response?.razorpay_order_id || paymentOrder.order_id,
            razorpay_payment_id: response?.razorpay_payment_id || '',
            razorpay_signature: response?.razorpay_signature || '',
          });
        },
        modal: {
          ondismiss: () => {
            if (settled) return;
            settled = true;
            reject(new Error('Payment was cancelled.'));
          },
        },
      });
      checkout.on('payment.failed', (response) => {
        if (settled) return;
        settled = true;
        const reason =
          response?.error?.description ||
          response?.error?.reason ||
          'Payment failed. Please try again or use another payment method.';
        checkout.close();
        reject(new Error(reason));
      });
      checkout.open();
    })
  );

  const handleSubmit = async () => {
    const errors = validateFormFields();
    setFieldErrors(errors);
    setPhoneError(errors.phone || '');
    const firstField = firstErrorField(errors);
    if (firstField) {
      setError('');
      scrollToField(firstField);
      return;
    }
    const payload = buildSubmitPayload();
    setSubmitting(true);
    setError('');
    setFieldErrors({});
    setPhoneError('');
    let paymentAttemptStarted = false;
    try {
      if (paymentRequired) {
        const paymentOrder = await startPublicWebCheckInPayment(token, payload);
        const scriptLoaded = await loadRazorpayScript();
        if (!scriptLoaded) {
          throw new Error('Unable to load Razorpay checkout. Please try again.');
        }
        paymentAttemptStarted = true;
        const razorpayResult = await openRazorpayCheckout(paymentOrder);
        await verifyPublicWebCheckInPayment(token, {
          razorpay_order_id: razorpayResult.razorpay_order_id,
          razorpay_payment_id: razorpayResult.razorpay_payment_id,
          razorpay_signature: razorpayResult.razorpay_signature,
        });
      } else {
        await submitPublicWebCheckIn(token, payload);
      }
      bumpDashboardDataRevision(paymentRequired ? 'web_checkin_payment_success' : 'web_checkin_success');
      setInvite((prev) => (
        prev
          ? {
            ...prev,
            status: 'submitted',
            ...(paymentRequired ? { payment_status: 'paid' } : {}),
          }
          : prev
      ));
      setCompletionVariant('success');
      setAiPromptOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const message = err?.userMessage || err?.message || 'Unable to submit web check-in.';
      setError(message);
      if (paymentRequired && paymentAttemptStarted) {
        setPaymentFailure(message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const fieldSx = useMemo(() => ({
    '& .MuiOutlinedInput-root': {
      color: '#f8fafc',
      backgroundColor: alpha('#ffffff', 0.035),
      '& fieldset': { borderColor: alpha('#ffffff', 0.16) },
      '&:hover fieldset': { borderColor: alpha(accent, 0.55) },
      '&.Mui-focused fieldset': { borderColor: accent },
      '&.Mui-error fieldset': { borderColor: '#f87171' },
    },
    '& .MuiInputLabel-root': {
      color: alpha('#ffffff', 0.66),
      '&.Mui-focused': { color: accent },
      '&.Mui-error': { color: '#fca5a5' },
    },
    '& .MuiFormHelperText-root': {
      marginLeft: 0,
      '&.Mui-error': { color: '#fca5a5' },
    },
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      filter: 'invert(1)',
    },
  }), []);

  const autocompleteFieldSx = {
    ...fieldSx,
    '& .MuiAutocomplete-inputRoot': {
      color: '#f8fafc',
      backgroundColor: alpha('#ffffff', 0.035),
    },
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
      color: '#cbd5e1',
    },
  };

  const uploadBox = (side, label, hasFile, fieldError = '') => {
    const previewSrc = side === 'front' ? guest.id_front : guest.id_back;
    const selectedFile = side === 'front' ? frontFile : backFile;
    const showError = Boolean(fieldError);
    return (
      <Box data-field={side === 'front' ? 'id_front' : 'id_back'}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => handleUploadClick(side)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') handleUploadClick(side);
        }}
        sx={{
          minHeight: hasFile ? 168 : 96,
          border: `1px dashed ${
            showError ? '#f87171' : hasFile ? accent : alpha('#ffffff', 0.32)
          }`,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          cursor: 'pointer',
          bgcolor: alpha('#ffffff', 0.04),
          color: '#cbd5e1',
          overflow: 'hidden',
          p: 1,
        }}
      >
        {hasFile && previewSrc ? (
          <Stack alignItems="center" spacing={0.75} sx={{ width: '100%' }}>
            <Box
              component="img"
              src={previewSrc}
              alt={`${label} preview`}
              sx={{
                width: '100%',
                maxHeight: 112,
                objectFit: 'contain',
                borderRadius: 1.5,
                bgcolor: alpha('#000000', 0.28),
                border: `1px solid ${alpha(accent, 0.28)}`,
              }}
            />
            <Typography variant="body2" sx={{ color: accent, fontWeight: 800 }}>
              {selectedFile?.name || `${label} selected`}
            </Typography>
            <Typography variant="caption">Tap to replace</Typography>
            <Button
              size="small"
              startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: '16px !important' }} />}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                clearDocumentSide(side);
              }}
              onKeyDown={(event) => event.stopPropagation()}
              sx={{
                mt: 0.25,
                minWidth: 0,
                px: 1.1,
                py: 0.3,
                color: '#fca5a5',
                fontWeight: 800,
                fontSize: '0.72rem',
                textTransform: 'none',
                bgcolor: alpha('#ef4444', 0.12),
                border: `1px solid ${alpha('#ef4444', 0.35)}`,
                borderRadius: 1.5,
                '&:hover': {
                  bgcolor: alpha('#ef4444', 0.22),
                  borderColor: alpha('#ef4444', 0.5),
                },
              }}
            >
              Remove
            </Button>
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={0.75}>
            {cameraUpload ? <CameraAltIcon sx={{ color: accent }} /> : <CloudUploadIcon sx={{ color: accent }} />}
            <Typography variant="body2">Upload {label}</Typography>
            <Typography variant="caption">Click to {cameraUpload ? 'open camera' : 'upload or drag and drop'}</Typography>
          </Stack>
        )}
      </Box>
      {showError ? (
        <FormHelperText error sx={{ mt: 0.75, ml: 0.25 }}>
          {fieldError}
        </FormHelperText>
      ) : null}
      </Box>
    );
  };

  const aiPromptDocumentRow = (side, title, helper, hasFile) => {
    const required = !helper;
    const previewSrc = side === 'front' ? guest.id_front : guest.id_back;
    const selectedFile = side === 'front' ? frontFile : backFile;
    return (
      <Box
        sx={{
          p: 1.35,
          borderRadius: 2.5,
          border: `1px solid ${hasFile ? alpha(accent, 0.45) : alpha('#ffffff', 0.1)}`,
          background: hasFile
            ? `linear-gradient(145deg, ${alpha(accent, 0.12)} 0%, ${alpha('#0f172a', 0.55)} 100%)`
            : `linear-gradient(145deg, ${alpha('#ffffff', 0.04)} 0%, ${alpha('#0b1220', 0.65)} 100%)`,
          boxShadow: hasFile ? `0 0 0 1px ${alpha(accent, 0.12)}, 0 12px 28px ${alpha(accent, 0.08)}` : 'none',
          transition: 'border-color 160ms ease, box-shadow 160ms ease',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.1 }}>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Typography sx={{ fontWeight: 800, color: '#f8fafc', fontSize: '0.88rem', letterSpacing: 0.1 }}>
              {title}
              {required ? (
                <Box component="span" sx={{ color: '#f87171', ml: 0.4 }}>*</Box>
              ) : (
                <Box component="span" sx={{ color: '#94a3b8', fontWeight: 600, ml: 0.6, fontSize: '0.75rem' }}>
                  {helper}
                </Box>
              )}
            </Typography>
          </Stack>
          {hasFile ? (
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label="Ready"
              sx={{
                height: 22,
                fontWeight: 800,
                fontSize: '0.68rem',
                color: '#031512',
                bgcolor: accent,
                '& .MuiChip-icon': { color: '#031512', ml: 0.5 },
              }}
            />
          ) : (
            <Chip
              size="small"
              label={required ? 'Required' : 'Optional'}
              sx={{
                height: 22,
                fontWeight: 700,
                fontSize: '0.68rem',
                color: required ? '#fdba74' : '#94a3b8',
                bgcolor: required ? alpha('#f59e0b', 0.14) : alpha('#ffffff', 0.06),
                border: `1px solid ${required ? alpha('#f59e0b', 0.35) : alpha('#ffffff', 0.1)}`,
              }}
            />
          )}
        </Stack>

        {hasFile && previewSrc ? (
          <Box
            sx={{
              position: 'relative',
              mb: 1.1,
              borderRadius: 2,
              overflow: 'hidden',
              border: `1px solid ${alpha(accent, 0.28)}`,
              bgcolor: alpha('#000000', 0.35),
            }}
          >
            <Box
              component="img"
              src={previewSrc}
              alt={`${title} preview`}
              sx={{
                display: 'block',
                width: '100%',
                maxHeight: 148,
                objectFit: 'contain',
              }}
            />
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{
                px: 1,
                py: 0.7,
                borderTop: `1px solid ${alpha('#ffffff', 0.08)}`,
                bgcolor: alpha('#020617', 0.72),
              }}
            >
              <Typography
                noWrap
                sx={{ color: '#cbd5e1', fontSize: '0.72rem', fontWeight: 600, minWidth: 0, flex: 1 }}
              >
                {selectedFile?.name || `${title} selected`}
              </Typography>
              <Button
                size="small"
                startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: '16px !important' }} />}
                onClick={() => clearDocumentSide(side)}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.25,
                  color: '#fca5a5',
                  fontWeight: 800,
                  fontSize: '0.72rem',
                  textTransform: 'none',
                  bgcolor: alpha('#ef4444', 0.12),
                  border: `1px solid ${alpha('#ef4444', 0.35)}`,
                  borderRadius: 1.5,
                  '&:hover': {
                    bgcolor: alpha('#ef4444', 0.22),
                    borderColor: alpha('#ef4444', 0.5),
                  },
                }}
              >
                Remove
              </Button>
            </Stack>
          </Box>
        ) : null}

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CloudUploadIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                if (side === 'front') frontInputRef.current?.click();
                if (side === 'back') backInputRef.current?.click();
              }}
              sx={{
                minHeight: 48,
                borderRadius: 2,
                color: hasFile ? accent : '#e2e8f0',
                borderColor: hasFile ? accent : alpha(accent, 0.35),
                bgcolor: alpha(accent, hasFile ? 0.1 : 0.04),
                fontWeight: 800,
                fontSize: '0.78rem',
                letterSpacing: 0.4,
                textTransform: 'none',
                '&:hover': {
                  borderColor: accent,
                  bgcolor: alpha(accent, 0.12),
                },
              }}
            >
              {hasFile ? 'Replace' : 'Upload'}
            </Button>
          </Grid>
          <Grid item xs={6}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CameraAltIcon sx={{ fontSize: 18 }} />}
              onClick={() => startCamera(side)}
              sx={{
                minHeight: 48,
                borderRadius: 2,
                color: '#e2e8f0',
                borderColor: alpha(accent, 0.35),
                bgcolor: alpha(accent, 0.04),
                fontWeight: 800,
                fontSize: '0.78rem',
                letterSpacing: 0.4,
                textTransform: 'none',
                '&:hover': {
                  borderColor: accent,
                  bgcolor: alpha(accent, 0.12),
                },
              }}
            >
              Camera
            </Button>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <ThemeProvider theme={webCheckInDarkTheme}>
    <LocalizationProvider dateAdapter={AdapterMoment}>
    <Box
      sx={{
        minHeight: '100vh',
        height: '100dvh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        color: '#f8fafc',
        p: { xs: 1.25, md: 3 },
        pb: { xs: 4, md: 3 },
        boxSizing: 'border-box',
        background: `
          radial-gradient(ellipse 80% 50% at 10% -10%, ${alpha(accent, 0.18)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 100% 0%, ${alpha('#38bdf8', 0.12)} 0%, transparent 50%),
          linear-gradient(180deg, #070b14 0%, #0b1120 48%, #0a1220 100%)
        `,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 1120,
          mx: 'auto',
          p: { xs: 1.5, md: 3 },
          borderRadius: { xs: 2.5, md: 3.5 },
          bgcolor: alpha('#111827', 0.88),
          border: `1px solid ${alpha('#ffffff', 0.1)}`,
          boxShadow: `0 30px 80px ${alpha('#000', 0.45)}, inset 0 1px 0 ${alpha('#ffffff', 0.04)}`,
          backdropFilter: 'blur(10px)',
          boxSizing: 'border-box',
          overflow: 'hidden',
        }}
      >
        <Stack spacing={{ xs: 1.75, md: 2.5 }} sx={{ width: '100%', minWidth: 0 }}>
          <Stack direction="row" spacing={1.1} alignItems="center">
            <Box
              sx={{
                width: { xs: 38, md: 44 },
                height: { xs: 38, md: 44 },
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                bgcolor: alpha(accent, 0.16),
                border: `1px solid ${alpha(accent, 0.35)}`,
                color: accent,
                flexShrink: 0,
              }}
            >
              <HowToRegRoundedIcon sx={{ fontSize: { xs: 22, md: 24 } }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.2rem', md: '1.55rem' }, letterSpacing: -0.4, lineHeight: 1.15 }}>
                {showCompletedPanel ? 'Check-in status' : 'Web Check-in'}
              </Typography>
              <Typography
                sx={{ color: '#94a3b8', mt: 0.2, fontSize: { xs: '0.78rem', md: '0.86rem' }, fontWeight: 500 }}
                noWrap
              >
                {invite?.hotel?.name || 'Hotel'}
                {invite?.room?.number ? ` · Room ${invite.room.number}` : ''}
                {invite?.room?.room_type_name ? ` · ${invite.room.room_type_name}` : ''}
              </Typography>
            </Box>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 8 }}>
              <CircularProgress sx={{ color: accent }} />
            </Stack>
          ) : showCompletedPanel ? (
            <CheckInCompletedPanel
              invite={invite}
              variant={completionVariant === 'success' ? 'success' : 'completed'}
              paid={Boolean(isPaidCompletedInvite || (completionVariant === 'success' && paymentRequired))}
            />
          ) : (
            <>
              <StaySummaryCard invite={invite} />

              {error ? <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert> : null}
              {showInactiveOnly ? (
                <Alert severity="warning" sx={{ borderRadius: 2 }}>
                  This web check-in link is no longer active. Please contact the hotel for assistance.
                </Alert>
              ) : null}

              {!showInactiveOnly ? (
              <>
              {canUseAiWebCheckIn && (
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  bgcolor: alpha(accent, 0.08),
                  border: `1px solid ${alpha(accent, 0.24)}`,
                  width: '100%',
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                }}
              >
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                  <AutoAwesomeIcon sx={{ color: accent, flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800 }}>AI Web Check-In</Typography>
                    <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                      Capture or upload ID document images, then let AI fill guest details.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    onClick={() => {
                      setError('');
                      setAiPromptOpen(true);
                    }}
                    disabled={aiLoading || isLinkInactive}
                    startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                    sx={{
                      bgcolor: accent,
                      color: '#031512',
                      fontWeight: 800,
                      flexShrink: 0,
                      alignSelf: { xs: 'stretch', md: 'center' },
                      '&:hover': { bgcolor: '#3bd1a7' },
                    }}
                  >
                    AI Web Check-In
                  </Button>
                </Stack>
              </Paper>
              )}

              {paymentRequired ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha('#f59e0b', 0.08),
                    border: `1px solid ${alpha('#f59e0b', 0.28)}`,
                  }}
                >
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontWeight: 900 }}>
                        {invite.payment_requirement === 'full' ? 'Full payment required' : 'Partial payment required'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
                        {invite.payment_requirement === 'full'
                          ? `Complete payment of ${paymentAmountLabel || 'the booking amount'} to finish web check-in.`
                          : `Pay ${paymentAmountLabel || 'the partial amount'} now. Total booking amount: ${bookingTotalLabel || 'not available'}.`}
                      </Typography>
                    </Box>
                    <Chip
                      label={paymentAmountLabel || 'Payment required'}
                      sx={{
                        bgcolor: alpha('#f59e0b', 0.16),
                        color: '#fbbf24',
                        border: `1px solid ${alpha('#f59e0b', 0.34)}`,
                        fontWeight: 900,
                      }}
                    />
                  </Stack>
                </Paper>
              ) : null}

              <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6} data-field="phone">
                  <TextField
                    label="Mobile Number"
                    value={guest.phone}
                    onChange={updatePhoneNumber}
                    onBlur={() => {
                      const nextError = validatePhoneForCountry(guest.phone, phoneCountryCode);
                      setPhoneError(nextError);
                      setFieldErrors((prev) => {
                        if (!nextError && !prev.phone) return prev;
                        if (!nextError) {
                          const next = { ...prev };
                          delete next.phone;
                          return next;
                        }
                        return { ...prev, phone: nextError };
                      });
                    }}
                    fullWidth
                    required
                    error={Boolean(fieldErrors.phone || phoneError)}
                    helperText={fieldErrors.phone || phoneError || `Enter ${formatPhoneLengthLabel(phoneCountryCode)}`}
                    sx={fieldSx}
                    InputProps={{
                      readOnly: phoneLocked,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            ref={phoneCountryCodeAnchorRef}
                            onClick={() => {
                              if (!phoneLocked) setPhoneCountryCodeMenuOpen(true);
                            }}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: phoneLocked ? 'default' : 'pointer',
                              gap: 0.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              opacity: phoneLocked ? 0.85 : 1,
                              '&:hover': phoneLocked
                                ? undefined
                                : { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                            }}
                          >
                            <Typography component="span" sx={{ fontSize: '1.2rem', lineHeight: 1 }}>
                              {selectedPhoneCountryCode?.flag || '🇮🇳'}
                            </Typography>
                            <Typography component="span" sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#e2e8f0' }}>
                              {selectedPhoneCountryCode?.dialCode || '+91'}
                            </Typography>
                            {!phoneLocked ? (
                              <Typography component="span" sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
                                ▼
                              </Typography>
                            ) : null}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{
                      inputMode: 'numeric',
                      pattern: '[0-9]*',
                      maxLength: getPhoneMaxLength(phoneCountryCode),
                    }}
                  />
                  <Menu
                    anchorEl={phoneCountryCodeAnchorRef.current}
                    open={phoneCountryCodeMenuOpen}
                    onClose={() => {
                      setPhoneCountryCodeMenuOpen(false);
                      setPhoneCountryCodeSearch('');
                    }}
                    PaperProps={{
                      sx: {
                        maxHeight: 400,
                        width: 300,
                        bgcolor: '#0f172a',
                        color: '#e2e8f0',
                        border: '1px solid rgba(148,163,184,0.24)',
                      },
                    }}
                  >
                    <Box sx={{ p: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search country..."
                        value={phoneCountryCodeSearch}
                        onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                        sx={{
                          mb: 1,
                          '& .MuiOutlinedInput-root': {
                            color: '#e2e8f0',
                            '& fieldset': { borderColor: 'rgba(148,163,184,0.35)' },
                          },
                          '& .MuiInputBase-input::placeholder': { color: '#94a3b8', opacity: 1 },
                        }}
                      />
                    </Box>
                    {filteredPhoneCountryCodes.map((country) => (
                      <MenuItem
                        key={`${country.code}-${country.dialCode}`}
                        onClick={() => selectPhoneCountryCode(country)}
                        selected={selectedPhoneCountryCode?.dialCode === country.dialCode}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Typography sx={{ fontSize: '1.2rem' }}>{country.flag}</Typography>
                        </ListItemIcon>
                        <ListItemText
                          primary={country.dialCode}
                          secondary={country.name}
                          primaryTypographyProps={{ fontWeight: 600, color: '#e2e8f0' }}
                          secondaryTypographyProps={{ color: '#94a3b8' }}
                        />
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid>
                <Grid item xs={12} md={6} data-field="fullName">
                  <Stack direction="row" spacing={1} sx={{ width: '100%', minWidth: 0 }}>
                    <FormControl sx={{ width: 96, flexShrink: 0, ...fieldSx }}>
                      <InputLabel>Title</InputLabel>
                      <Select label="Title" value={guest.title} onChange={updateGuest('title')}>
                        {titleOptions.map((title) => (
                          <MenuItem key={title || 'none'} value={title}>{title || 'Title'}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Name"
                      value={guest.fullName}
                      onChange={updateGuest('fullName')}
                      fullWidth
                      required
                      error={Boolean(fieldErrors.fullName)}
                      helperText={fieldErrors.fullName || ' '}
                      sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={6} data-field="email">
                  <TextField
                    label="Email Address"
                    value={guest.email}
                    onChange={updateGuest('email')}
                    fullWidth
                    required
                    type="email"
                    error={Boolean(fieldErrors.email)}
                    helperText={fieldErrors.email || ' '}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3} data-field="gender">
                  <FormControl fullWidth required error={Boolean(fieldErrors.gender)} sx={fieldSx}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      label="Gender"
                      value={guest.gender}
                      onChange={(event) => {
                        clearFieldError('gender');
                        setGuest((prev) => ({ ...prev, gender: event.target.value }));
                      }}
                    >
                      <MenuItem value="">Gender</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                    </Select>
                    <FormHelperText>{fieldErrors.gender || ' '}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3} data-field="dob">
                  <DatePicker
                    label="Date of Birth"
                    value={guest.dob ? moment(guest.dob, 'YYYY-MM-DD') : null}
                    onChange={(date) => {
                      const nextDob = date && date.isValid() ? date.format('YYYY-MM-DD') : '';
                      setGuest((prev) => ({
                        ...prev,
                        dob: nextDob,
                      }));
                      const dobError = validateDateOfBirth(nextDob);
                      setFieldErrors((prev) => {
                        if (!dobError && !prev.dob) return prev;
                        if (!dobError) {
                          const next = { ...prev };
                          delete next.dob;
                          return next;
                        }
                        return { ...prev, dob: dobError };
                      });
                    }}
                    maxDate={moment().subtract(18, 'years')}
                    disableFuture
                    format="DD/MM/YYYY"
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: Boolean(fieldErrors.dob),
                        helperText: fieldErrors.dob || 'Must be at least 18 years old',
                        sx: fieldSx,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Guest Address" value={guest.address} onChange={updateGuest('address')} fullWidth multiline minRows={2} sx={fieldSx} />
                </Grid>
                <Grid item xs={12} md={3} data-field="country">
                  <Autocomplete
                    options={countries}
                    getOptionLabel={(option) => option?.name || ''}
                    filterOptions={countryFilterOptions}
                    value={countries.find((country) => country.name === guest.country || country.code === guest.country) || null}
                    onChange={(event, value) => applyCountryChange(value?.name || '')}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        required
                        error={Boolean(fieldErrors.country)}
                        helperText={fieldErrors.country || ' '}
                        sx={autocompleteFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} data-field="state">
                  <Autocomplete
                    options={availableStates}
                    getOptionLabel={(option) => option?.name || ''}
                    filterOptions={stateFilterOptions}
                    value={availableStates.find((state) => state.name === guest.state || state.code === guest.state) || null}
                    onChange={(event, value) => {
                      clearFieldErrors(['state', 'city']);
                      setGuest((prev) => ({
                        ...prev,
                        state: value?.name || '',
                        city: '',
                      }));
                    }}
                    disabled={!selectedCountryObj}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        required
                        error={Boolean(fieldErrors.state)}
                        helperText={fieldErrors.state || ' '}
                        sx={autocompleteFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} data-field="city">
                  <Autocomplete
                    options={cityOptions}
                    getOptionLabel={(option) => (option != null ? String(option) : '')}
                    filterOptions={cityFilterOptions}
                    value={cityOptions.find((city) => String(city).trim() === String(guest.city || '').trim()) || null}
                    onChange={(event, value) => {
                      clearFieldError('city');
                      setGuest((prev) => ({
                        ...prev,
                        city: value != null ? String(value) : '',
                      }));
                    }}
                    disabled={!selectedStateObj}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        required
                        error={Boolean(fieldErrors.city)}
                        helperText={fieldErrors.city || ' '}
                        sx={autocompleteFieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3} data-field="pincode">
                  <TextField
                    label="Pincode"
                    value={guest.pincode}
                    onChange={updateGuest('pincode')}
                    fullWidth
                    required
                    error={Boolean(fieldErrors.pincode)}
                    helperText={fieldErrors.pincode || ' '}
                    sx={fieldSx}
                  />
                </Grid>
              </Grid>
              </Box>

              <Box data-field="purpose_of_stay">
                <Typography variant="body2" sx={{ color: '#cbd5e1', mb: 1 }}>
                  Purpose of stay <Box component="span" sx={{ color: '#ef4444' }}>*</Box>
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {purposeOptions.map((option) => (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={() => {
                        clearFieldErrors(['purpose_of_stay', 'purpose_of_stay_note']);
                        setGuest((prev) => ({ ...prev, purpose_of_stay: option.value }));
                      }}
                      sx={{
                        color: guest.purpose_of_stay === option.value ? '#031512' : '#e2e8f0',
                        bgcolor: guest.purpose_of_stay === option.value ? accent : alpha('#ffffff', 0.06),
                        border: `1px solid ${
                          fieldErrors.purpose_of_stay
                            ? '#f87171'
                            : guest.purpose_of_stay === option.value
                              ? accent
                              : alpha('#ffffff', 0.18)
                        }`,
                      }}
                    />
                  ))}
                </Stack>
                {fieldErrors.purpose_of_stay ? (
                  <FormHelperText error sx={{ mt: 0.75, ml: 0.25 }}>
                    {fieldErrors.purpose_of_stay}
                  </FormHelperText>
                ) : null}
                {guest.purpose_of_stay === 'other' ? (
                  <Box data-field="purpose_of_stay_note" sx={{ mt: 1.5 }}>
                    <TextField
                      label="Describe purpose"
                      value={guest.purpose_of_stay_note}
                      onChange={updateGuest('purpose_of_stay_note')}
                      fullWidth
                      required
                      error={Boolean(fieldErrors.purpose_of_stay_note)}
                      helperText={fieldErrors.purpose_of_stay_note || ' '}
                      sx={fieldSx}
                    />
                  </Box>
                ) : null}
              </Box>

              <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={6} data-field="document_type">
                  <FormControl fullWidth required error={Boolean(fieldErrors.document_type)} sx={fieldSx}>
                    <InputLabel>Select Document Type</InputLabel>
                    <Select label="Select Document Type" value={guest.document_type} onChange={updateDocumentType}>
                      {documentOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>{fieldErrors.document_type || ' '}</FormHelperText>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
                    <Typography variant="body2" sx={{ color: '#cbd5e1' }}>Camera upload</Typography>
                    <Switch checked={cameraUpload} onChange={(e) => setCameraUpload(e.target.checked)} />
                  </Stack>
                </Grid>
                <Grid item xs={12} data-field="id_number">
                  <TextField
                    label={isAadhaarDocumentType(guest.document_type) ? 'Aadhaar Card Number' : 'ID Number'}
                    value={isAadhaarDocumentType(guest.document_type)
                      ? formatAadhaarDisplay(guest.id_number)
                      : guest.id_number}
                    onChange={updateDocumentIdNumber}
                    fullWidth
                    required
                    error={Boolean(fieldErrors.id_number)}
                    helperText={fieldErrors.id_number || ' '}
                    sx={fieldSx}
                    placeholder={isAadhaarDocumentType(guest.document_type) ? 'XXXX-XXXX-XXXX' : 'Enter ID number'}
                    inputProps={isAadhaarDocumentType(guest.document_type)
                      ? {
                        inputMode: 'numeric',
                        pattern: '[0-9\\-]*',
                        maxLength: 14,
                      }
                      : undefined}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {uploadBox(
                    'front',
                    `Document Front${
                      !guest.id_front && !guest.id_back && guest.document_type && guest.document_type !== 'no_document'
                        ? ' *'
                        : ''
                    }`,
                    Boolean(guest.id_front),
                    fieldErrors.id_front,
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  {uploadBox(
                    'back',
                    `Document Back${
                      !guest.id_front && !guest.id_back && guest.document_type && guest.document_type !== 'no_document'
                        ? ' *'
                        : ''
                    }`,
                    Boolean(guest.id_back),
                    fieldErrors.id_back,
                  )}
                </Grid>
              </Grid>
              </Box>

              <input ref={frontInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect('front')} />
              <input ref={backInputRef} type="file" accept="image/*" hidden onChange={handleFileSelect('back')} />

              <Box sx={{ width: '100%' }}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={submitting || isLinkInactive}
                  onClick={handleSubmit}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <CheckCircleIcon />}
                  sx={{
                    width: '100%',
                    minHeight: 52,
                    borderRadius: 2.25,
                    bgcolor: accent,
                    color: '#031512',
                    fontWeight: 900,
                    px: 2,
                    fontSize: '0.95rem',
                    letterSpacing: 0.2,
                    textTransform: 'uppercase',
                    boxShadow: `0 12px 28px ${alpha(accent, 0.28)}`,
                    '&:hover': { bgcolor: '#3bd1a7' },
                    '&.Mui-disabled': {
                      bgcolor: alpha(accent, 0.28),
                      color: alpha('#031512', 0.5),
                      boxShadow: 'none',
                    },
                  }}
                >
                  {paymentRequired ? 'Continue to Payment' : 'Submit Web Check-in'}
                </Button>
              </Box>
              </>
              ) : null}
            </>
          )}
        </Stack>
      </Paper>

      <Dialog
        open={Boolean(canUseAiWebCheckIn && aiPromptOpen && !loading && !isLinkInactive)}
        onClose={() => setAiPromptOpen(false)}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        TransitionComponent={Fade}
        transitionDuration={220}
        sx={{
          zIndex: AI_PROMPT_DIALOG_Z,
          '& .MuiDialog-container': {
            alignItems: 'center',
            px: 1.5,
            py: 1.5,
          },
        }}
        PaperProps={{
          sx: {
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: '#0b1220',
            color: '#f8fafc',
            borderRadius: 4,
            m: 0,
            width: { xs: '100%', sm: '100%' },
            maxWidth: { xs: '100%', sm: 480 },
            maxHeight: 'calc(100dvh - 24px)',
            border: `1px solid ${alpha(accent, 0.35)}`,
            boxShadow: `
              0 0 0 1px ${alpha(accent, 0.08)},
              0 28px 80px ${alpha('#000000', 0.65)},
              0 0 60px ${alpha(accent, 0.12)}
            `,
            backgroundImage: `
              radial-gradient(ellipse 80% 55% at 12% -10%, ${alpha(accent, 0.28)} 0%, transparent 55%),
              radial-gradient(ellipse 55% 40% at 100% 0%, ${alpha('#38bdf8', 0.14)} 0%, transparent 50%),
              linear-gradient(180deg, #101828 0%, #0b1220 48%, #080d18 100%)
            `,
          },
        }}
        BackdropProps={{
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: alpha('#020617', 0.72),
          },
        }}
      >
        <DialogTitle
          sx={{
            p: 0,
            flexShrink: 0,
            color: '#f8fafc',
            bgcolor: 'transparent',
            background: 'transparent',
            borderBottom: `1px solid ${alpha(accent, 0.16)}`,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ px: 2, py: 1.4 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2.25,
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0,
                background: `linear-gradient(145deg, ${alpha(accent, 0.95)} 0%, ${alpha('#22d3ee', 0.75)} 100%)`,
                color: '#031512',
                boxShadow: `0 8px 22px ${alpha(accent, 0.35)}`,
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={0.85} alignItems="center" useFlexGap flexWrap="wrap">
                <Typography sx={{ fontWeight: 900, fontSize: '1.05rem', letterSpacing: -0.2, lineHeight: 1.15 }}>
                  AI Web Check-In
                </Typography>
                <Chip
                  label="Gemini"
                  size="small"
                  sx={{
                    height: 20,
                    color: accent,
                    bgcolor: alpha(accent, 0.12),
                    border: `1px solid ${alpha(accent, 0.28)}`,
                    fontWeight: 900,
                    fontSize: '0.68rem',
                    letterSpacing: 0.3,
                  }}
                />
              </Stack>
              <Typography sx={{ mt: 0.25, color: alpha('#e2e8f0', 0.7), fontSize: '0.72rem', fontWeight: 500 }}>
                Scan ID · auto-fill guest details
              </Typography>
            </Box>
            <IconButton
              onClick={() => setAiPromptOpen(false)}
              size="small"
              sx={{
                color: alpha('#f8fafc', 0.75),
                bgcolor: alpha('#ffffff', 0.04),
                '&:hover': { bgcolor: alpha(accent, 0.14), color: accent },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2,
            py: 1.75,
            flex: '1 1 auto',
            minHeight: 0,
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            bgcolor: 'transparent',
            backgroundColor: 'transparent',
          }}
        >
          <Stack spacing={1.35}>
            <Box
              sx={{
                px: 1.25,
                py: 1,
                borderRadius: 2,
                border: `1px solid ${alpha(accent, 0.18)}`,
                bgcolor: alpha(accent, 0.06),
              }}
            >
              <Typography sx={{ color: '#cbd5e1', lineHeight: 1.4, fontWeight: 500, fontSize: '0.78rem' }}>
                Front is used for AI fill. On the guest form, either front or back document image is enough.
              </Typography>
            </Box>
            {aiPromptDocumentRow('front', 'Front side', '', Boolean(guest.id_front))}
            {aiPromptDocumentRow('back', 'Back side', '(optional)', Boolean(guest.id_back))}
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            flexShrink: 0,
            px: 2,
            pt: 1.25,
            pb: 1.75,
            display: 'block',
            borderTop: `1px solid ${alpha('#ffffff', 0.08)}`,
            bgcolor: alpha('#020617', 0.55),
            backgroundColor: alpha('#020617', 0.55),
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={handleAiAutofill}
            disabled={aiLoading || !frontFile}
            startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesomeIcon />}
            sx={{
              minHeight: 48,
              borderRadius: 2.5,
              bgcolor: accent,
              color: '#031512',
              fontWeight: 900,
              fontSize: '0.95rem',
              textTransform: 'none',
              letterSpacing: 0.1,
              boxShadow: `0 12px 28px ${alpha(accent, 0.35)}`,
              backgroundImage: `linear-gradient(135deg, ${accent} 0%, #2dd4bf 55%, #22d3ee 100%)`,
              '&:hover': {
                bgcolor: '#3bd1a7',
                backgroundImage: `linear-gradient(135deg, #3bd1a7 0%, ${accent} 55%, #22d3ee 100%)`,
                boxShadow: `0 14px 32px ${alpha(accent, 0.42)}`,
              },
              '&.Mui-disabled': {
                bgcolor: alpha(accent, 0.28),
                backgroundImage: 'none',
                color: alpha('#031512', 0.48),
                boxShadow: 'none',
              },
            }}
          >
            {aiLoading ? 'Reading document…' : 'Read with AI & fill form'}
          </Button>
          <Button
            fullWidth
            onClick={() => setAiPromptOpen(false)}
            sx={{
              color: alpha('#e2e8f0', 0.78),
              mt: 0.85,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.82rem',
              '&:hover': { color: accent, bgcolor: alpha(accent, 0.08) },
            }}
          >
            Close and fill manually
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(cameraTarget)}
        onClose={stopCamera}
        fullWidth
        maxWidth="sm"
        sx={{ zIndex: NESTED_DIALOG_Z }}
        PaperProps={{
          sx: {
            bgcolor: '#0b1220',
            color: '#f8fafc',
            border: `1px solid ${alpha(accent, 0.28)}`,
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: alpha('#020617', 0.78),
          },
        }}
      >
        <DialogTitle sx={{ color: '#f8fafc', bgcolor: 'transparent', background: 'transparent' }}>
          Capture Document {cameraTarget === 'front' ? 'Front' : 'Back'}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'transparent', backgroundColor: 'transparent' }}>
          <video ref={videoRef} style={{ width: '100%', borderRadius: 12, background: '#000' }} playsInline muted />
          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </DialogContent>
        <DialogActions sx={{ bgcolor: alpha('#020617', 0.55), backgroundColor: alpha('#020617', 0.55) }}>
          <Button onClick={stopCamera} sx={{ color: '#e2e8f0' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={captureCamera}
            sx={{ bgcolor: accent, color: '#031512', fontWeight: 800, '&:hover': { bgcolor: '#3bd1a7' } }}
          >
            Capture
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(paymentFailure)}
        onClose={() => setPaymentFailure('')}
        sx={{ zIndex: NESTED_DIALOG_Z }}
      >
        <DialogTitle>Payment Not Completed</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} alignItems="center" sx={{ py: 1, maxWidth: 360 }}>
            <CloseIcon sx={{ fontSize: 54, color: '#ef4444' }} />
            <Typography align="center">
              {paymentFailure || 'Payment could not be completed. Please try again.'}
            </Typography>
            <Typography variant="body2" align="center" sx={{ color: '#64748b' }}>
              You are still on the web check-in form. Tap Continue to Payment to try again.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setPaymentFailure('')}>Try Again</Button>
        </DialogActions>
      </Dialog>
    </Box>
    </LocalizationProvider>
    </ThemeProvider>
  );
}
